import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ImprovBrief,
  PerformerStatus,
  RhythmCatalogEntry,
  CatalogEntryId,
} from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { BriefPatch } from '../api/improvClient.js';
import { useImprovClient } from '../hooks/useImprovClient.js';
import { useBriefQueue } from '../hooks/useBriefQueue.js';
import { orderBriefs, pickActiveBrief } from '../lib/brief.js';

/**
 * Large stage brief view for the Performance Console (§20.14). Big, dark, glanceable display of the
 * active improv brief with previous/next queue controls, connectivity/freshness, and a single
 * "Performed" action. Performer edit controls (§20.13) let the performer set exact BPM, choose a
 * rhythm pattern/variation/kit (free text or from a loaded catalog), edit key/meter, add a private
 * note, mark status, and derive a new brief that combines these settings with another brief's topic.
 *
 * §20.14: NO audience identity or moderation controls appear here. Editing never alters the original
 * audience choices — `rawChoices` is shown read-only alongside the edit-history count (§20.13).
 */

export interface BriefConsoleViewProps {
  gigId: string;
  /** Artist owning the gig; used to load rhythm catalogs for exact-pattern pickers (optional). */
  artistId: string | null;
  /** Whether the gig is live/in-intermission — drives polling, matching the interaction queue. */
  active: boolean;
  /** Browser connectivity — state-changing actions are disabled while offline (§24.5). */
  online: boolean;
}

function tempoText(brief: ImprovBrief): string {
  const t = brief.resolved.tempo;
  if (!t) return '—';
  if (t.targetBpm != null) return `${t.targetBpm} BPM`;
  if (t.minBpm != null && t.maxBpm != null) {
    return t.label ? `${t.label} · ${t.minBpm}–${t.maxBpm} BPM` : `${t.minBpm}–${t.maxBpm} BPM`;
  }
  return t.label ?? '—';
}

function keyText(brief: ImprovBrief): string {
  const k = brief.resolved.key;
  if (!k) return '—';
  return k.display || `${k.tonic} ${k.mode}`.trim();
}

function meterText(brief: ImprovBrief): string {
  const m = brief.resolved.meter;
  if (!m) return '—';
  return m.display || `${m.numerator}/${m.denominator}`;
}

function grooveText(brief: ImprovBrief): string {
  return brief.resolved.rhythm?.family || '—';
}

/** Exact device selection line, shown only when an exact pattern/variation/kit is chosen (§20.14). */
function deviceText(brief: ImprovBrief): string | null {
  const r = brief.resolved.rhythm;
  if (!r) return null;
  const parts: string[] = [];
  if (r.catalogEntryId) parts.push(r.catalogEntryId);
  if (r.variation) parts.push(`var ${r.variation}`);
  if (r.kitId) parts.push(`kit ${r.kitId}`);
  if (parts.length === 0) return null;
  return `${r.deviceProfileKey ? `${r.deviceProfileKey}: ` : ''}${parts.join(' · ')}`;
}

function freshnessLabel(lastUpdatedAt: number | null, now: number): string {
  if (!lastUpdatedAt) return 'never';
  const secs = Math.max(0, Math.round((now - lastUpdatedAt) / 1000));
  if (secs < 2) return 'just now';
  return `${secs}s ago`;
}

interface EditDraft {
  targetBpm: string;
  tonic: string;
  mode: string;
  numerator: string;
  denominator: string;
  family: string;
  catalogEntryId: string;
  variation: string;
  kitId: string;
  deviceProfileKey: string;
  privateNote: string;
}

function draftFrom(brief: ImprovBrief): EditDraft {
  const t = brief.resolved.tempo;
  const k = brief.resolved.key;
  const m = brief.resolved.meter;
  const r = brief.resolved.rhythm;
  return {
    targetBpm: t?.targetBpm != null ? String(t.targetBpm) : '',
    tonic: k?.tonic ?? '',
    mode: k?.mode ?? '',
    numerator: m ? String(m.numerator) : '',
    denominator: m ? String(m.denominator) : '',
    family: r?.family ?? '',
    catalogEntryId: r?.catalogEntryId ?? '',
    variation: r?.variation ?? '',
    kitId: r?.kitId ?? '',
    deviceProfileKey: r?.deviceProfileKey ?? '',
    privateNote: brief.privatePerformerNote ?? '',
  };
}

/** Build a {@link BriefPatch} from an edit draft, sending only groups the performer filled in. */
function patchFrom(draft: EditDraft): BriefPatch {
  const patch: BriefPatch = {};
  const bpm = Number.parseInt(draft.targetBpm, 10);
  if (draft.targetBpm.trim() && Number.isFinite(bpm)) {
    patch.tempo = { targetBpm: bpm, resolution: 'audience_exact' };
  }
  if (draft.tonic.trim() && draft.mode.trim()) {
    patch.key = {
      tonic: draft.tonic.trim(),
      mode: draft.mode.trim(),
      display: `${draft.tonic.trim()} ${draft.mode.trim()}`,
    };
  }
  const num = Number.parseInt(draft.numerator, 10);
  const den = Number.parseInt(draft.denominator, 10);
  if (Number.isFinite(num) && num > 0 && Number.isFinite(den) && den > 0) {
    patch.meter = { numerator: num, denominator: den, display: `${num}/${den}` };
  }
  const rhythm: NonNullable<BriefPatch['rhythm']> = {};
  if (draft.family.trim()) rhythm.family = draft.family.trim();
  if (draft.catalogEntryId.trim()) {
    rhythm.catalogEntryId = draft.catalogEntryId.trim() as CatalogEntryId;
  }
  if (draft.variation.trim()) rhythm.variation = draft.variation.trim();
  if (draft.kitId.trim()) rhythm.kitId = draft.kitId.trim();
  if (draft.deviceProfileKey.trim()) rhythm.deviceProfileKey = draft.deviceProfileKey.trim();
  if (Object.keys(rhythm).length > 0) patch.rhythm = rhythm;
  patch.privatePerformerNote = draft.privateNote.trim() ? draft.privateNote.trim() : null;
  return patch;
}

const STATUS_ACTIONS: ReadonlyArray<{ status: PerformerStatus; label: string; cls?: string }> = [
  { status: 'accepted', label: 'Accept' },
  { status: 'queued', label: 'Queue' },
  { status: 'hidden', label: 'Hide', cls: 'qbtn--hide' },
];

export function BriefConsoleView({ gigId, artistId, active, online }: BriefConsoleViewProps) {
  const client = useImprovClient();
  const queue = useBriefQueue(gigId, active);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [entries, setEntries] = useState<RhythmCatalogEntry[]>([]);
  const [topicSourceId, setTopicSourceId] = useState('');

  const ordered = useMemo(() => orderBriefs(queue.briefs), [queue.briefs]);
  const activeBrief = useMemo(
    () => pickActiveBrief(queue.briefs, currentId),
    [queue.briefs, currentId],
  );
  const activeIndex = activeBrief
    ? ordered.findIndex((b) => b.briefId === activeBrief.briefId)
    : -1;

  // Keep the freshness label ticking, matching the console header.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load rhythm catalogs once so the exact-pattern pickers can offer verified entries (§20.9).
  useEffect(() => {
    if (!artistId) return;
    let cancelled = false;
    void (async () => {
      try {
        const catalogs = await client.listCatalogs(artistId);
        if (cancelled) return;
        setEntries(catalogs.filter((c) => c.status === 'active').flatMap((c) => c.entries));
      } catch {
        // Catalog pickers are optional; free-text entry still works if this fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, artistId]);

  const startEdit = useCallback(() => {
    if (!activeBrief) return;
    setDraft(draftFrom(activeBrief));
    setEditing(true);
    setActionError(null);
  }, [activeBrief]);

  const mutate = useCallback(
    async (fn: () => Promise<ImprovBrief>) => {
      if (!online) return;
      setBusy(true);
      setActionError(null);
      try {
        const updated = await fn();
        queue.applyLocal(updated);
        setCurrentId(updated.briefId);
        return updated;
      } catch (err) {
        setActionError(err instanceof ApiError ? err.message : 'Action failed.');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [online, queue],
  );

  const saveEdit = useCallback(async () => {
    if (!activeBrief || !draft) return;
    const updated = await mutate(() =>
      client.patchBrief(gigId, activeBrief.briefId, patchFrom(draft)),
    );
    if (updated) setEditing(false);
  }, [activeBrief, draft, mutate, client, gigId]);

  const setStatus = useCallback(
    (status: PerformerStatus) => {
      if (!activeBrief) return;
      void mutate(() => client.patchBrief(gigId, activeBrief.briefId, { status }));
    },
    [activeBrief, mutate, client, gigId],
  );

  const derive = useCallback(() => {
    if (!activeBrief || !topicSourceId) return;
    void mutate(() => client.deriveBrief(gigId, activeBrief.briefId, topicSourceId));
  }, [activeBrief, topicSourceId, mutate, client, gigId]);

  const goto = useCallback(
    (delta: number) => {
      if (activeIndex < 0) return;
      const next = ordered[activeIndex + delta];
      if (next) {
        setCurrentId(next.briefId);
        setEditing(false);
      }
    },
    [activeIndex, ordered],
  );

  const patchDraft = (key: keyof EditDraft, value: string) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const connLabel =
    queue.connectivity === 'online'
      ? 'Connected'
      : queue.connectivity === 'offline'
        ? 'Offline'
        : 'Reconnecting…';

  // Topic sources: other briefs that carry a topic (§20.13 combine).
  const topicSources = useMemo(
    () => queue.briefs.filter((b) => b.topic && b.briefId !== activeBrief?.briefId),
    [queue.briefs, activeBrief],
  );

  const requiredPhrase = activeBrief?.rawChoices['requiredPhrase'] ?? null;
  const device = activeBrief ? deviceText(activeBrief) : null;

  return (
    <section className="brief-stage" aria-label="Improv brief stage view">
      <div className="brief-stage__head">
        <h2>Improv brief</h2>
        <div className="brief-stage__health" aria-live="polite">
          <span className={`conn conn--${queue.connectivity}`}>{connLabel}</span>
          <span className="muted">updated {freshnessLabel(queue.lastUpdatedAt, now)}</span>
          {ordered.length > 0 && activeIndex >= 0 && (
            <span className="muted">
              {activeIndex + 1} / {ordered.length}
            </span>
          )}
        </div>
      </div>

      {!active && (
        <p className="hint">Brief polling pauses until the gig is live or in intermission.</p>
      )}
      {actionError && <p className="error">{actionError}</p>}

      {!activeBrief ? (
        <p className="muted brief-stage__empty">No improv brief to perform yet.</p>
      ) : (
        <>
          <div className="brief-card" aria-live="polite">
            <p className="brief-card__summary">{activeBrief.friendlySummary}</p>

            <dl className="brief-grid">
              <div className="brief-grid__cell">
                <dt>Tempo</dt>
                <dd className="brief-grid__big">{tempoText(activeBrief)}</dd>
              </div>
              <div className="brief-grid__cell">
                <dt>Key</dt>
                <dd className="brief-grid__big">{keyText(activeBrief)}</dd>
              </div>
              <div className="brief-grid__cell">
                <dt>Meter</dt>
                <dd className="brief-grid__big">{meterText(activeBrief)}</dd>
              </div>
              <div className="brief-grid__cell">
                <dt>Groove</dt>
                <dd className="brief-grid__big">{grooveText(activeBrief)}</dd>
              </div>
            </dl>

            {device && (
              <p className="brief-card__device">
                <span className="eyebrow small">Exact device</span> {device}
              </p>
            )}
            {activeBrief.topic && (
              <p className="brief-card__topic">
                <span className="eyebrow small">Topic</span> {activeBrief.topic}
              </p>
            )}
            {requiredPhrase && (
              <p className="brief-card__topic">
                <span className="eyebrow small">Required phrase</span> {requiredPhrase}
              </p>
            )}
            {activeBrief.privatePerformerNote && (
              <p className="brief-card__note">
                <span className="eyebrow small">Private note</span>{' '}
                {activeBrief.privatePerformerNote}
              </p>
            )}
          </div>

          <div className="brief-stage__nav">
            <button
              type="button"
              className="big ghost"
              disabled={activeIndex <= 0}
              onClick={() => goto(-1)}
            >
              ‹ Previous
            </button>
            <button
              type="button"
              className="big now-item__complete brief-stage__performed"
              disabled={!online || busy}
              onClick={() => setStatus('performed')}
            >
              Performed
            </button>
            <button
              type="button"
              className="big ghost"
              disabled={activeIndex < 0 || activeIndex >= ordered.length - 1}
              onClick={() => goto(1)}
            >
              Next ›
            </button>
          </div>

          <div className="brief-stage__actions">
            {STATUS_ACTIONS.map((a) => (
              <button
                key={a.status}
                type="button"
                className={`qbtn ${a.cls ?? ''}`}
                disabled={!online || busy}
                onClick={() => setStatus(a.status)}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              className="qbtn"
              disabled={busy}
              onClick={() => (editing ? setEditing(false) : startEdit())}
            >
              {editing ? 'Close editor' : 'Edit'}
            </button>
          </div>

          {editing && draft && (
            <form
              className="brief-edit"
              onSubmit={(e) => {
                e.preventDefault();
                void saveEdit();
              }}
            >
              <div className="brief-edit__grid">
                <label>
                  Exact BPM
                  <input
                    type="number"
                    min={40}
                    max={240}
                    value={draft.targetBpm}
                    onChange={(e) => patchDraft('targetBpm', e.target.value)}
                  />
                </label>
                <label>
                  Tonic
                  <input value={draft.tonic} onChange={(e) => patchDraft('tonic', e.target.value)} />
                </label>
                <label>
                  Mode
                  <input value={draft.mode} onChange={(e) => patchDraft('mode', e.target.value)} />
                </label>
                <label>
                  Meter numerator
                  <input
                    type="number"
                    min={1}
                    value={draft.numerator}
                    onChange={(e) => patchDraft('numerator', e.target.value)}
                  />
                </label>
                <label>
                  Meter denominator
                  <input
                    type="number"
                    min={1}
                    value={draft.denominator}
                    onChange={(e) => patchDraft('denominator', e.target.value)}
                  />
                </label>
                {entries.length > 0 && (
                  <label>
                    Catalog pattern
                    <select
                      value={draft.catalogEntryId}
                      onChange={(e) => {
                        const entry = entries.find((x) => x.catalogEntryId === e.target.value);
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                catalogEntryId: e.target.value,
                                family: entry?.family ?? d.family,
                                deviceProfileKey: entry?.deviceProfileKey ?? d.deviceProfileKey,
                              }
                            : d,
                        );
                      }}
                    >
                      <option value="">— free text —</option>
                      {entries.map((entry) => (
                        <option key={entry.catalogEntryId} value={entry.catalogEntryId}>
                          {entry.displayName} ({entry.family})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Groove family
                  <input
                    value={draft.family}
                    onChange={(e) => patchDraft('family', e.target.value)}
                  />
                </label>
                <label>
                  Variation
                  <input
                    value={draft.variation}
                    onChange={(e) => patchDraft('variation', e.target.value)}
                    placeholder="e.g. A"
                  />
                </label>
                <label>
                  Drum kit
                  <input value={draft.kitId} onChange={(e) => patchDraft('kitId', e.target.value)} />
                </label>
              </div>
              <label>
                Private performer note
                <textarea
                  rows={2}
                  value={draft.privateNote}
                  onChange={(e) => patchDraft('privateNote', e.target.value)}
                  maxLength={2000}
                />
              </label>
              <div className="button-row">
                <button type="submit" className="action--primary" disabled={!online || busy}>
                  {busy ? 'Saving…' : 'Save edits'}
                </button>
                <button type="button" className="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
              {!online && <p className="hint">Offline — edits are paused until you reconnect.</p>}
            </form>
          )}

          <div className="brief-derive">
            <label>
              Combine with topic from
              <select value={topicSourceId} onChange={(e) => setTopicSourceId(e.target.value)}>
                <option value="">— pick a brief —</option>
                {topicSources.map((b) => (
                  <option key={b.briefId} value={b.briefId}>
                    {b.topic}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="qbtn"
              disabled={!online || busy || !topicSourceId}
              onClick={derive}
            >
              Derive new brief
            </button>
          </div>

          <details className="brief-raw">
            <summary>
              Audience choices (read-only) · {activeBrief.editHistory.length} edit
              {activeBrief.editHistory.length === 1 ? '' : 's'}
            </summary>
            <p className="muted small">
              Original audience choices are preserved. Editing records history and never changes
              them (§20.13).
            </p>
            <dl className="brief-raw__list">
              {Object.entries(activeBrief.rawChoices).map(([dim, value]) => (
                <div key={dim} className="brief-raw__row">
                  <dt>{dim}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </details>
        </>
      )}
    </section>
  );
}
